// Catalog.ID Client — The factor inputs (PIN pad + six-digit code field)
// ============================================================================
// device-setup-restructure §6.2 and §15. ONE implementation of "type the
// factors named here", mounted by both screens that ask for them:
//
//   * the unlock screen (auth/login-view.ts `showFactorUnlock`), which asks for
//     the factors that RELEASE this device's encryption key, and
//   * the mode-change confirm step (components/device-mode-confirm.ts), which
//     asks for the factors a downgrade REMOVES.
//
// They ask for the same two things, are refused in the same two ways, and the
// rule about what survives a refusal is the same rule — so it lives here once.
// Extracted from `showFactorUnlock` on 2026-09-18 with its behaviour intact:
//
//   * submits by itself the moment every named factor is complete (nobody
//     presses a button for a PIN they have just finished typing), and never
//     re-enters while an attempt is in flight;
//   * a PIN refusal resets the pad and keeps the code, a code refusal clears
//     only the code — the caller decides which, through {@link FactorInputs.keep},
//     because only it has the node's answer;
//   * a repaint (`setFactors`) keeps the typed code and starts the pad empty.
//
// DOM only. Every decision about WHICH factors and WHAT a refusal means lives
// in auth/device-unlock.ts and auth/device-mode-change.ts.

import { createElement } from '@catalog-shell';
import { createPinEntry, resetPinEntry } from './pin-entry';
import { isCompleteAuthenticatorCode, normalizeAuthenticatorCode } from '../auth/authenticator';
import type { UnlockFactor } from '../auth/device-unlock';

/** What the member has typed. A field absent means "not typed", never "". */
export interface FactorValues {
    pin?: string;
    code?: string;
}

export interface FactorInputsOptions {
    /** What to paint, in the node's order — PIN first, as it is verified first. */
    factors: readonly UnlockFactor[];
    /** The pad length to start on. */
    pinLength: 4 | 6 | 8;
    /**
     * Offer the 4 / 6 / 8 chooser above the pad. Both live screens do: the node
     * never returns a device's PIN length, so the only record is this browser's
     * own localStorage and it can be stale or absent.
     */
    pinLengthChooser?: boolean;
    /**
     * Fires once every named factor is complete. Re-entry is suppressed until
     * it settles, so a half-typed retry cannot burn a PIN attempt or a code.
     */
    onSubmit(values: FactorValues): Promise<void>;
}

export interface FactorInputs {
    /** Put this in the page. Repaints happen inside it. */
    readonly element: HTMLElement;
    /** The pad length the member is typing at — the chooser moves it. */
    readonly pinLength: 4 | 6 | 8;
    /** What is painted right now. */
    readonly factors: readonly UnlockFactor[];
    /** Repaint for the factor list the node has just named. Keeps the code. */
    setFactors(factors: readonly UnlockFactor[]): void;
    /**
     * After a refusal: keep exactly what survives the retry and clear the rest.
     * A field left `undefined` is emptied on screen — the pad is reset, the code
     * field cleared and focused — so the member's next keystroke goes where the
     * failure actually was.
     */
    keep(values: FactorValues): void;
}

export function createFactorInputs(options: FactorInputsOptions): FactorInputs {
    let factors: readonly UnlockFactor[] = options.factors;
    let pinLength: 4 | 6 | 8 = options.pinLength;
    let pin: string | undefined;
    let code = '';
    let busy = false;

    let pinEntry: HTMLElement | null = null;
    let codeInput: HTMLInputElement | null = null;

    const host = createElement('div', { className: 'cid-factor-inputs' });

    function render(): void {
        host.innerHTML = '';
        pinEntry = null;
        codeInput = null;

        if (factors.includes('pin')) {
            if (options.pinLengthChooser) {
                const lengthRow = createElement('div', { className: 'cid-auth-pin-length' });
                for (const len of [4, 6, 8] as const) {
                    const btn = createElement('button', {
                        className: `cs-btn ${len === pinLength ? 'cs-btn--yes' : 'cs-btn--default'} cid-auth-pin-len-btn`,
                        textContent: `${len} digits`,
                    });
                    btn.addEventListener('click', () => {
                        pinLength = len;
                        // A PIN half-typed at the old length is not a PIN at the
                        // new one — drop it rather than carry digits across.
                        pin = undefined;
                        render();
                    });
                    lengthRow.appendChild(btn);
                }
                host.appendChild(lengthRow);
            }

            pinEntry = createPinEntry({
                pinLength,
                onComplete: (entered) => {
                    pin = entered;
                    void maybeSubmit();
                },
            });
            host.appendChild(pinEntry);
        }

        if (factors.includes('totp')) {
            const field = createElement('div', { className: 'cid-auth-field' });
            const input = document.createElement('input');
            input.type = 'text';
            input.className = 'cid-auth-input';
            input.placeholder = '000000';
            input.inputMode = 'numeric';
            input.autocomplete = 'one-time-code';
            input.maxLength = 6;
            input.value = code;
            input.addEventListener('input', () => {
                const normalized = normalizeAuthenticatorCode(input.value);
                if (input.value !== normalized) input.value = normalized;
                code = normalized;
                void maybeSubmit();
            });
            input.addEventListener('keydown', (e) => {
                if ((e as KeyboardEvent).key === 'Enter') void maybeSubmit();
            });
            field.appendChild(input);
            host.appendChild(field);
            codeInput = input;
            // The PIN pad takes the focus when both are shown — it is asked
            // first and the node verifies it first.
            if (!factors.includes('pin')) input.focus();
        }
    }

    /** Fire only when EVERY named factor is complete — a half-filled attempt
     *  would burn a PIN try or a code attempt for nothing. */
    async function maybeSubmit(): Promise<void> {
        if (busy) return;
        if (factors.includes('pin') && pin === undefined) return;
        if (factors.includes('totp') && !isCompleteAuthenticatorCode(code)) return;

        busy = true;
        try {
            await options.onSubmit({ pin, code: code || undefined });
        } finally {
            busy = false;
        }
    }

    render();

    return {
        element: host,
        get pinLength() { return pinLength; },
        get factors() { return factors; },
        setFactors(next) {
            factors = next;
            render();
        },
        keep(values) {
            pin = values.pin;
            code = values.code ?? '';
            if (pin === undefined && pinEntry) resetPinEntry(pinEntry);
            if (codeInput && codeInput.value !== code) {
                codeInput.value = code;
                codeInput.focus();
            }
        },
    };
}
